export type TaskStatus = 'pending' | 'in_progress' | 'done'

type Props = {
  status: TaskStatus
  color?: string
  size?: number
  className?: string
}

export function TaskStatusIcon({
  status,
  color = 'var(--primary)',
  size = 24,
  className = '',
}: Props) {
  const strokeWidth = size <= 20 ? 2 : 2.5
  const center = size / 2
  const radius = center - strokeWidth - 1
  const circ = 2 * Math.PI * radius

  const offset = status === 'pending' ? circ : status === 'in_progress' ? circ * 0.5 : 0

  const isDone = status === 'done'
  const checkSize = Math.round(size * 0.52)

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={className}
      role="img"
      aria-label={
        status === 'pending'
          ? 'Task pending'
          : status === 'in_progress'
            ? 'Task in progress'
            : 'Task complete'
      }
      style={{ display: 'block', flexShrink: 0, overflow: 'visible' }}
    >
      {/* Track — always visible as the circle border */}
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeOpacity={0.2}
        strokeWidth={strokeWidth}
      />

      {/* Filled disk — fades in when done */}
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill={color}
        style={{
          opacity: isDone ? 1 : 0,
          transition: 'opacity 0.25s ease 0.4s',
        }}
      />

      {/* Progress arc — stays visible at all states; acts as border ring when done */}
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circ}
        strokeDashoffset={offset}
        style={{
          transform: 'rotate(-90deg)',
          transformOrigin: `${center}px ${center}px`,
          transition: 'stroke-dashoffset 0.55s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      />

      {/* Checkmark — fades in after disk */}
      <text
        x={center}
        y={center}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={checkSize}
        fontWeight="800"
        className="fill-white dark:fill-[#111]"
        style={{
          opacity: isDone ? 1 : 0,
          transition: 'opacity 0.15s ease 0.55s',
          userSelect: 'none',
        }}
      >
        ✓
      </text>
    </svg>
  )
}
